/** An AutomationTemplate holds a reusable automation blueprint with template-specific metadata. */
export interface AutomationTemplate {
  /**
   * AutomationTemplate ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number. This increments by 1 whenever the automation template is updated. Specify the current revision number whenever updating an existing automation template.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the AutomationTemplate was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the AutomationTemplate was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /** The automation blueprint this template is based on. */
  automation?: Automation;
  /**
   * Component ID that owns this template.
   * @format GUID
   * @readonly
   */
  componentId?: string | null;
  /**
   * Component version.
   * @readonly
   */
  componentVersion?: number | null;
  /**
   * App ID that owns this template.
   * @format GUID
   * @readonly
   */
  appId?: string;
  /**
   * Display name for the automation template.
   * @minLength 1
   * @maxLength 500
   */
  name?: string;
  /**
   * description of what the automation template does.
   * @minLength 1
   * @maxLength 2000
   */
  description?: string;
  /**
   * goal or intent of the automation template.
   * @maxLength 500
   */
  goal?: string | null;
}

export enum Category {
  SELL_MORE_ONLINE = 'SELL_MORE_ONLINE',
  ENGAGE_WITH_CLIENTS = 'ENGAGE_WITH_CLIENTS',
  GET_PAID_ON_TIME = 'GET_PAID_ON_TIME',
  MANAGE_BUSINESS = 'MANAGE_BUSINESS',
  RETAIN_CUSTOMERS = 'RETAIN_CUSTOMERS',
  POPULAR = 'POPULAR',
}

/** @enumType */
export type CategoryWithLiterals =
  | Category
  | 'SELL_MORE_ONLINE'
  | 'ENGAGE_WITH_CLIENTS'
  | 'GET_PAID_ON_TIME'
  | 'MANAGE_BUSINESS'
  | 'RETAIN_CUSTOMERS'
  | 'POPULAR';

export interface Automation extends AutomationOriginInfoOneOf {
  /** When the origin of the automation is `APPLICATION`, the details of the app that created it. */
  applicationInfo?: ApplicationOrigin;
  /** When the origin of the automation is `PREINSTALLED`, the details of the preinstalled automation. */
  preinstalledInfo?: PreinstalledOrigin;
  /** When the origin of the automation is `TEMPLATE`, the details of the template automation. */
  templateInfo?: TemplateOrigin;
  /**
   * Automation ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number. This increments by 1 whenever the automation is updated. Specify the current revision number whenever updating an existing automation.
   * @readonly
   */
  revision?: string | null;
  /**
   * Who created the automation.
   * @immutable
   * @readonly
   */
  createdBy?: AuditInfo;
  /**
   * When the automation was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Who last updated the automation.
   * @readonly
   */
  updatedBy?: AuditInfo;
  /**
   * When the automation was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Automation name as displayed on the user's site.
   * @minLength 1
   * @maxLength 500
   */
  name?: string;
  /**
   * Automation description.
   * @maxLength 2000
   */
  description?: string | null;
  /** Automation configuration. */
  configuration?: AutomationConfiguration;
  /**
   * How the automation was added to the user's site.
   * @immutable
   */
  origin?: OriginWithLiterals;
  /** Automation settings. Applied only for pre-installed and application automations. Not applied to User origin automations */
  settings?: AutomationSettings;
  /**
   * When the automation is a draft, the draft details.
   * @readonly
   */
  draftInfo?: DraftInfo;
  /**
   * Whether the automation is archived.
   *
   * To archive an automation, set this to `true`. To restore an archived automation, set this to `false`.
   */
  archived?: boolean;
  /** Auto archive policy */
  autoArchivePolicy?: AutoArchivePolicy;
}

/** @oneof */
export interface AutomationOriginInfoOneOf {
  /** When the origin of the automation is `APPLICATION`, the details of the app that created it. */
  applicationInfo?: ApplicationOrigin;
  /** When the origin of the automation is `PREINSTALLED`, the details of the preinstalled automation. */
  preinstalledInfo?: PreinstalledOrigin;
  /** When the origin of the automation is `TEMPLATE`, the details of the template automation. */
  templateInfo?: TemplateOrigin;
}

export interface AIMetadata {
  /**
   * Origin agent identifier.
   * @minLength 1
   * @maxLength 64
   */
  originAgentIdentifier?: string | null;
}

export interface ActionSettings {
  /**
   * IDs of actions whose `skipActionExpression` can't be modified.
   *
   * An action's `skipActionExpression` determines whether the action is skipped. Including an action's ID in this array makes its `skipActionExpression` permanent, so it can't be changed.
   *
   * When empty, the `skipActionExpression` of all actions can be edited.
   * @maxSize 30
   * @format GUID
   */
  permanentActionIds?: string[];
  /**
   * IDs of read-only actions. Read-only actions cannot be modified.
   *
   * When empty and the automation's `settings.readonly` is `false`, all actions are modifiable.
   * @maxSize 30
   * @format GUID
   */
  readonlyActionIds?: string[];
}

export enum Domain {
  /** User domain (default). */
  USER = 'USER',
  /** Wix domain. */
  WIX = 'WIX',
  /** Wix account-level domain. */
  WIX_ACCOUNT = 'WIX_ACCOUNT',
}

/** @enumType */
export type DomainWithLiterals = Domain | 'USER' | 'WIX' | 'WIX_ACCOUNT';

export interface TriggerSettings {}

export interface Enrichment {
  /**
   * Enrichment input mappings.
   * @maxSize 2
   */
  inputMappings?: Record<string, any>[] | null;
}

export interface AuditInfo extends AuditInfoIdOneOf {
  /**
   * [User ID](https://dev.wix.com/docs/build-apps/get-started/overview/glossary#user-id).
   * @format GUID
   */
  userId?: string;
  /**
   * [App ID](https://dev.wix.com/docs/build-apps/get-started/overview/glossary#app-id).
   *
   * You can find the app ID on the [Home page](https://manage.wix.com/app-selector?title=Select+an+App&primaryButtonText=Select+Site&actionUrl=https%3A%2F%2Fdev.wix.com%2Fapps%2F%7BappId%7D%home) in the app's dashboard.
   * @format GUID
   */
  appId?: string;
}

/** @oneof */
export interface AuditInfoIdOneOf {
  /**
   * [User ID](https://dev.wix.com/docs/build-apps/get-started/overview/glossary#user-id).
   * @format GUID
   */
  userId?: string;
  /**
   * [App ID](https://dev.wix.com/docs/build-apps/get-started/overview/glossary#app-id).
   *
   * You can find the app ID on the [Home page](https://manage.wix.com/app-selector?title=Select+an+App&primaryButtonText=Select+Site&actionUrl=https%3A%2F%2Fdev.wix.com%2Fapps%2F%7BappId%7D%home) in the app's dashboard.
   * @format GUID
   */
  appId?: string;
}

export interface AutomationConfiguration {
  /** Status of the automation on the site. */
  status?: StatusWithLiterals;
  /** Trigger configuration. */
  trigger?: Trigger;
  /**
   * Root action IDs. A root action is the first action that runs after the trigger occurs. Root actions run in parallel.
   *
   * > **Note**: You can currently only specify 1 root action.
   * @maxSize 20
   * @format GUID
   */
  rootActionIds?: string[];
  /** Actions the automation can execute, as `key:value` pairs. For the key, specify the action ID. The value must be an object structured as described below. */
  actions?: Record<string, Action>;
}

export enum TimeUnit {
  /** Minutes. */
  MINUTES = 'MINUTES',
  /** Hours. */
  HOURS = 'HOURS',
  /** Days. */
  DAYS = 'DAYS',
  /** Weeks. */
  WEEKS = 'WEEKS',
  /** Months. */
  MONTHS = 'MONTHS',
}

/** @enumType */
export type TimeUnitWithLiterals =
  | TimeUnit
  | 'MINUTES'
  | 'HOURS'
  | 'DAYS'
  | 'WEEKS'
  | 'MONTHS';

export interface Filter {
  /**
   * Filter ID.
   * @format GUID
   */
  id?: string;
  /**
   * Field key.
   *
   * You can find the field key in [the trigger payload schema](https://dev.wix.com/docs/rest/business-management/automations/triggers/the-trigger-payload-schema).
   * @minLength 1
   * @maxLength 110
   */
  fieldKey?: string;
  /**
   * Filter expression in bracket notation.
   *
   * For the automation to run, the expression must evaluate to `true`.
   * @maxLength 5000
   */
  filterExpression?: string;
}

export interface FutureDateActivationOffset {
  /**
   * Amount of time before the trigger to run the automation.
   *
   * > **Note**: To delay an automation after the trigger event occurs, use a [delay action](https://dev.wix.com/docs/rest/business-management/automations/automations/automations-v2/configure-your-automation#delay-action).
   * @maxLength 1000
   */
  preScheduledEventOffsetExpression?: string;
  /** Unit in which to set the action offset. */
  scheduledEventOffsetTimeUnit?: TimeUnitWithLiterals;
}

export interface RateLimit {
  /**
   * Maximum number of times the trigger can be activated.
   * @maxLength 1000
   */
  maxActivationsExpression?: string;
  /**
   * Duration of the rate limit. The rate limit applies for the specified duration and then expires.
   *
   * When empty, the rate limit does not expire.
   * @maxLength 1000
   */
  durationExpression?: string | null;
  /** Unit in which to set the duration of the rate limit. */
  durationTimeUnit?: TimeUnitWithLiterals;
  /**
   * Activation identifier used to count the number of activations.
   * @minLength 1
   * @maxLength 400
   */
  uniqueIdentifierExpression?: string | null;
}

export interface FilterValueSelection {
  /**
   * Values that can help the user filter certain automations. Specify values in the following format: `<filter_id>__<selected_value>`.
   * @maxLength 80
   * @maxSize 50
   */
  selectedFilterValues?: string[];
}

export interface ConditionExpressionGroup {
  /** Logical operator used to evaluate the condition expressions. */
  operator?: OperatorWithLiterals;
  /**
   * Expressions evaluated using the selected operator.
   * @minSize 1
   * @maxSize 20
   * @maxLength 5000
   */
  booleanExpressions?: string[];
}

export enum Operator {
  /** `OR` operator. */
  OR = 'OR',
  /** `AND` operator. */
  AND = 'AND',
}

/** @enumType */
export type OperatorWithLiterals = Operator | 'OR' | 'AND';

export interface CodeSnippet {
  /** Logical operator used to evaluate the condition expressions. */
  language?: LanguageWithLiterals;
  /**
   * Expressions evaluated using the selected operator. this code should comply the language syntax. and format
   * @maxLength 1500
   */
  code?: string;
}

export enum Language {
  /** JavaScript. */
  JAVASCRIPT = 'JAVASCRIPT',
}

/** @enumType */
export type LanguageWithLiterals = Language | 'JAVASCRIPT';

/** Path definition */
export interface Path {
  /**
   * Unique path ID.
   * @format GUID
   */
  id?: string;
  /**
   * Path name.
   * @minLength 1
   * @maxLength 100
   */
  name?: string;
  /**
   * ID of the action to run when this path is taken.
   * @format GUID
   */
  postActionId?: string | null;
  /**
   * Editable display name for the path.
   * @minLength 1
   * @maxLength 60
   */
  displayName?: string | null;
}

export enum Type {
  /** Defined by a [Wix app](https://dev.wix.com/docs/rest/business-management/automations/automations/automations-v2/configure-your-automation#app-defined-action). This action type is available in the site dashboard. */
  APP_DEFINED = 'APP_DEFINED',
  /**
   * Condition action. Evaluates a condition to determine which action runs next.
   *
   * Learn more about [adding and setting up conditions](https://support.wix.com/en/article/the-new-automation-builder-understanding-conditions-operators) in Wix Automations.
   *
   * > **Note**: When `skipActionExpression` evaluates to `true`, the actions in `conditionInfo.truePostActionIds` run and those in `conditionInfo.falsePostActionIds` are skipped.
   */
  CONDITION = 'CONDITION',
  /**
   * Code condition action. Evaluates custom code to determine which action runs next.
   *
   * Learn more about [adding and setting up conditions](https://support.wix.com/en/article/the-new-automation-builder-understanding-conditions-operators) in Wix Automations.
   *
   * > **Note**: When `skipActionExpression` evaluates to `true`, the actions in `codeConditionInfo.truePostActionIds` run and those in `codeConditionInfo.falsePostActionIds` are skipped.
   */
  CODE_CONDITION = 'CODE_CONDITION',
  /** Delay. The automation must wait before executing the next step. */
  DELAY = 'DELAY',
  /** Rate-limiter. This stops the automation flow if the subsequent action has reached its maximum allowed executions. */
  RATE_LIMIT = 'RATE_LIMIT',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'APP_DEFINED'
  | 'CONDITION'
  | 'CODE_CONDITION'
  | 'DELAY'
  | 'RATE_LIMIT';

export interface AppDefinedAction {
  /**
   * ID of the app that defines the action.
   * @format GUID
   */
  appId?: string;
  /**
   * Action key.
   * @minLength 1
   * @maxLength 100
   */
  actionKey?: string;
  /**
   * Action input mapping as defined in the action's [input schema](https://dev.wix.com/docs/rest/business-management/automations/actions/the-action-input-schema).
   *
   * The input mapping must include all parameters specified in the action's input schema's `required` array, and can include any other parameters from the schema.
   */
  inputMapping?: Record<string, any> | null;
  /**
   * Action skip conditions. An action is skipped if any of the specified conditions evaluates to `true`.
   *
   * > **Note**: Actions that follow a skipped action still run.
   * @maxSize 10
   * @deprecated Action skip conditions. An action is skipped if any of the specified conditions evaluates to `true`.
   *
   * > **Note**: Actions that follow a skipped action still run.
   * @replacedBy skip_action_expression
   * @targetRemovalDate 2026-02-01
   */
  skipConditionOrExpressionGroups?: ConditionExpressionGroup[];
  /**
   * IDs of actions that run in parallel after the action completes.
   * @maxSize 1
   * @format GUID
   */
  postActionIds?: string[];
  /** Action output schema. When specified, this replaces the action schema. */
  overrideOutputSchema?: Record<string, any> | null;
}

export interface ConditionAction {
  /**
   * Condition is `true` if one or more of the expression groups evaluates to `true`.
   * @minSize 1
   * @maxSize 10
   */
  orExpressionGroups?: ConditionExpressionGroup[];
  /**
   * IDs of actions to run when the condition evaluates to `true`.
   * @maxSize 1
   * @format GUID
   */
  truePostActionIds?: string[];
  /**
   * IDs of actions to run when the condition evaluates to `false`.
   * @maxSize 1
   * @format GUID
   */
  falsePostActionIds?: string[];
  /**
   * ID of the MERGE action for this condition. When absent, branches run independently (current behavior).
   * @format GUID
   */
  mergeActionId?: string | null;
}

export interface CodeConditionAction {
  /** condition code. This is a code that can be used to identify the condition in the automation. code should return a boolean value. otherwise the value will be tried to be casted to boolean. */
  snippet?: CodeSnippet;
  /**
   * dynamic variables used in the code snippet. these variables can be used inside the code using var('variableName') function.
   * this list is mainly for validation purpose.
   * @maxSize 50
   * @maxLength 100
   */
  dynamicVariableExpressions?: string[];
  /**
   * IDs of actions to run when the condition evaluates to `true`.
   * @maxSize 1
   * @format GUID
   */
  truePostActionIds?: string[];
  /**
   * IDs of actions to run when the condition evaluates to `false`.
   * @maxSize 1
   * @format GUID
   */
  falsePostActionIds?: string[];
  /**
   * ID of the MERGE action for this condition. When absent, branches run independently (current behavior).
   * @format GUID
   */
  mergeActionId?: string | null;
}

export interface DelayAction {
  /**
   * Time to wait before running the action. The wait time starts from when the current action completes.
   *
   * > **Note**: To define the wait time from a specific moment in time, use `dueDateExpression`.
   * @maxLength 1000
   */
  offsetExpression?: string | null;
  /** Unit in which to set the wait time to wait before the action runs. */
  offsetTimeUnit?: TimeUnitWithLiterals;
  /**
   * Action run date in milliseconds elapsed since January 1, 1970 UTC.
   *
   * > **Note**: If an `offsetExpression` is defined, the delay is calculated from the time of the offset.
   * @maxLength 1000
   * @deprecated Action run date in milliseconds elapsed since January 1, 1970 UTC.
   *
   * > **Note**: If an `offsetExpression` is defined, the delay is calculated from the time of the offset.
   * @replacedBy due_date_expression
   * @targetRemovalDate 2026-02-01
   */
  dueDateEpochExpression?: string | null;
  /**
   * Action run date as a timestamp or DateTime expression using bracket notation.
   *
   * > **Note**: If an `offsetExpression` is defined, the delay is calculated from the time of the offset.
   * @maxLength 1000
   */
  dueDateExpression?: string | null;
  /**
   * IDs of actions to run in parallel after the time delay.
   * @maxSize 1
   * @format GUID
   */
  postActionIds?: string[];
}

export interface RateLimitAction {
  /**
   * Maximum number of times the action can run.
   * @maxLength 1000
   */
  maxActivationsExpression?: string;
  /**
   * Rate limit duration.
   *
   * When empty, the rate limit does not expire.
   * @maxLength 1000
   */
  rateLimitDurationExpression?: string | null;
  /** Unit in which to set the duration of the rate limit. */
  rateLimitDurationTimeUnit?: TimeUnitWithLiterals;
  /**
   * Unique identifier of each activation by which rate limiter counts activations.
   * @minLength 1
   * @maxLength 400
   */
  uniqueIdentifierExpression?: string | null;
  /**
   * IDs of actions to run in parallel after the action completes.
   * @maxSize 1
   * @format GUID
   */
  postActionIds?: string[];
}

export interface SetVariablesAction {
  /** Output mapping. For example: `{"someField": "{{10}}", "someOtherField": "{{multiply( var('account.points.balance') ;2 )}}" }`. */
  outputMapping?: Record<string, any> | null;
  /**
   * Output JSON schema representation.
   *
   * > **Note**: To minimize request size, you can also specify a string.
   */
  outputSchema?: Record<string, any> | null;
  /**
   * IDs of actions to run in parallel after variable initialization.
   * @maxSize 1
   * @format GUID
   */
  postActionIds?: string[];
}

export interface OutputAction {
  /** Output action output mapping. */
  outputMapping?: Record<string, any> | null;
}

export interface SplitAction {
  /**
   * List of paths to split execution into.
   * @minSize 2
   * @maxSize 10
   */
  paths?: Path[];
}

export interface MergeAction {
  /**
   * ID of the action to execute after the arriving branch completes.
   * @format GUID
   */
  postActionId?: string | null;
}

export enum Status {
  /** Active. Active automations can be triggered. */
  ACTIVE = 'ACTIVE',
  /** Inactive. Inactive automations cannot be triggered. */
  INACTIVE = 'INACTIVE',
}

/** @enumType */
export type StatusWithLiterals = Status | 'ACTIVE' | 'INACTIVE';

export interface Trigger {
  /**
   * ID of the app that defines the trigger.
   * @format GUID
   */
  appId?: string;
  /**
   * Trigger key.
   *
   * Learn about [setting up a trigger](https://dev.wix.com/docs/rest/business-management/automations/triggers/add-a-trigger-to-your-app#step-1--set-up-the-trigger).
   * @minLength 1
   * @maxLength 100
   */
  triggerKey?: string;
  /**
   * Schema field filters. All filter conditions must be met for the automation to run.
   *
   * Learn more about setting up [automation filters](https://dev.wix.com/docs/rest/business-management/automations/automations/automations-v2/configure-your-automation#filters).
   * @maxSize 5
   */
  filters?: Filter[];
  /**
   * Automation offset. You can schedule automations to run before the trigger occurs.
   *
   * Learn more about [scheduled events](https://dev.wix.com/docs/rest/business-management/automations/automations/automations-v2/configure-your-automation#scheduled-events).
   */
  scheduledEventOffset?: FutureDateActivationOffset;
  /** Limit on the number of times an automation can be triggered. */
  rateLimit?: RateLimit;
  /** Trigger schema override. When specified, this replaces the trigger schema. */
  overrideSchema?: Record<string, any> | null;
}

export interface Action extends ActionInfoOneOf {
  /** Details of the action whose `type` is `APP_DEFINED`. */
  appDefinedInfo?: AppDefinedAction;
  /** Details of the action whose `type` is `CONDITION`. */
  conditionInfo?: ConditionAction;
  /** Details of the action whose `type` is `CODE_CONDITION`. */
  codeConditionInfo?: CodeConditionAction;
  /** Details of the action whose `type` is `DELAY`. */
  delayInfo?: DelayAction;
  /** Details of the action whose `type` is `RATE_LIMIT`. */
  rateLimitInfo?: RateLimitAction;
  /**
   * Action ID.
   *
   * Unique identifier for the action instance in the current automation. Use this to refer to this action in the automation flow.
   *
   * If not specified, automatically generated by Wix.
   * @format GUID
   */
  id?: string | null;
  /** [Action type](https://dev.wix.com/docs/rest/business-management/automations/automations/automations-v2/configure-your-automation#action-type). */
  type?: TypeWithLiterals;
  /**
   * Editable display name for the action.
   * @minLength 1
   * @maxLength 60
   */
  displayName?: string | null;
  /**
   * [Action namespace](https://dev.wix.com/docs/rest/business-management/automations/automations/automations-v2/configure-your-automation#namespace). This differentiates it from other actions of the same type.
   *
   * If the action outputs any data, the data appears under the namespace in the payload sent to the subsequent steps in the automation. If the user has multiple actions with the same `appId` and `actionKey`, the output of the previous action is overwritten.
   * @minLength 1
   * @maxLength 100
   */
  namespace?: string | null;
  /**
   * Evaluated to determine whether the action is skipped. When evaluated to `true`, the action is skipped. Otherwise, the action runs.
   *
   * When empty, the action runs.
   * @maxLength 1000
   */
  skipActionExpression?: string | null;
}

/** @oneof */
export interface ActionInfoOneOf {
  /** Details of the action whose `type` is `APP_DEFINED`. */
  appDefinedInfo?: AppDefinedAction;
  /** Details of the action whose `type` is `CONDITION`. */
  conditionInfo?: ConditionAction;
  /** Details of the action whose `type` is `CODE_CONDITION`. */
  codeConditionInfo?: CodeConditionAction;
  /** Details of the action whose `type` is `DELAY`. */
  delayInfo?: DelayAction;
  /** Details of the action whose `type` is `RATE_LIMIT`. */
  rateLimitInfo?: RateLimitAction;
}

export interface FilterableAppDefinedActions {
  /**
   * App-defined action identifiers. Identifiers have the following format: `${appId}_${actionKey}`.
   * @minSize 1
   * @maxSize 100
   * @minLength 1
   * @maxLength 150
   */
  actionIdentifiers?: string[];
}

export enum Origin {
  /** Created by a [Wix user](https://dev.wix.com/docs/build-apps/get-started/overview/glossary#wix-user). */
  USER = 'USER',
  /** Created by a [Wix app](https://dev.wix.com/docs/build-apps/get-started/overview/glossary#wix-app) for a particular site. */
  APPLICATION = 'APPLICATION',
  /** [Preinstalled automation](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/automations/pre-installed-automations/about-pre-installed-automations). */
  PREINSTALLED = 'PREINSTALLED',
}

/** @enumType */
export type OriginWithLiterals =
  | Origin
  | 'USER'
  | 'APPLICATION'
  | 'PREINSTALLED';

export interface ApplicationOrigin {
  /**
   * [App ID](https://dev.wix.com/docs/build-apps/get-started/overview/glossary#app-id).
   *
   * You can find the app ID on the [Home page](https://manage.wix.com/app-selector?title=Select+an+App&primaryButtonText=Select+Site&actionUrl=https%3A%2F%2Fdev.wix.com%2Fapps%2F%7BappId%7D%home) in the app's dashboard.
   * @format GUID
   */
  appId?: string;
}

export interface PreinstalledOrigin {
  /**
   * ID of the app that added the [preinstalled automation](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/automations/pre-installed-automations/about-pre-installed-automations).
   * @format GUID
   */
  appId?: string;
  /**
   * Whether the automation is an override.
   *
   * When a user modifies the preinstalled automation installed on their site, a site-specific version of the automation is created that overrides the automation that was originally installed. This override automation is assigned a **new** automation ID — it does not reuse the ID of the original preinstalled automation.
   *
   * > **Note**: Once an override exists, [Get Automation](https://dev.wix.com/docs/api-reference/business-management/automations/automations/automations-v2/get-automation) on the *original* preinstalled automation's ID returns `404 Not Found`. Use [Query Automations](https://dev.wix.com/docs/api-reference/business-management/automations/automations/automations-v2/query-automations) (for example, by `configuration.trigger.triggerKey`) to find the current automation, or retrieve `automationId` from the activation log.
   * >
   * > An override automation can no longer be updated by the app that originally installed it.
   * >
   * > To revert to the original preinstalled version, the user must delete the override by calling the Delete Automation method. Calling Delete Automation on an override automation removes the override and restores the preinstalled automation that was previously installed, once again accessible via its original ID.
   *
   * Default: `false`.
   * @immutable
   * @readonly
   */
  override?: boolean | null;
}

export interface TemplateOrigin {
  /**
   * ID of the app that added the template automation.
   * @format GUID
   */
  appId?: string;
}

export interface AutomationSettings {
  /**
   * Whether the automation is hidden from users.
   *
   * Default: `false`
   */
  hidden?: boolean;
  /**
   * Whether the automation is read-only.
   *
   * When `true`, site owners can't modify the automation or any of its actions. When `false`, users can configure specific actions as skippable or read-only using `settings.actionSettings`.
   *
   * Default: `false`.
   *
   * > **Note**: Setting `readOnly` to `true` overrides `settings.actionSettings`.
   */
  readonly?: boolean;
  /**
   * Whether to disable the option to delete the automation from the site.
   *
   * Default: `false`.
   */
  disableDelete?: boolean;
  /**
   * Whether to disable the option to change the automation's `configuration.status`.
   *
   * Default: `false`.
   */
  disableStatusChange?: boolean;
  /** Automation action settings. */
  actionSettings?: ActionSettings;
}

export interface DraftInfo {
  /**
   * ID of the original automation.
   * @format GUID
   * @readonly
   */
  originalAutomationId?: string | null;
}

export interface Enrichments {
  /** Whether the studio site enrichment is wanted. */
  studioSite?: Enrichment;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface AutoArchivePolicy {
  /**
   * Date when to archive the automation
   * If this date in the past, nothing will happen (automation will not go into archived state)
   * If this date in the future, on this date the automation will be updated with archived = true and configuration.status = INACTIVE
   * After this date the automation may be unarchived and archived again, this date will have no influence
   */
  archiveDate?: Date | null;
}

export interface Notes {}

export interface Note {}

export interface Position {}

export interface Offset {}

export interface Dimensions {}

/** A category with its order for ordering the template within the category. */
export interface CategoryInfo {
  /** The category. */
  category?: CategoryWithLiterals;
}

/** Information needed to create or resolve a segment from an automation template. */
export interface SegmentInfo {
  /** Segment template JSON that may contain placeholder values. */
  segmentTemplate?: Record<string, any> | null;
  /**
   * Automation action values keyed by segment template parameter.
   * @maxSize 100
   */
  templateParameterOverrides?: Record<string, any>;
}

export interface AutomationTemplateDeletedWithEntity {
  /** The AutomationTemplate that was deleted. */
  automationTemplate?: AutomationTemplate;
}

export interface CreateAutomationTemplateRequest {
  /** AutomationTemplate to be created. */
  automationTemplate: AutomationTemplate;
  /**
   * ID of the app to which the automation template belongs.
   * @format GUID
   */
  appId: string;
  /**
   * The meta site ID from which the original automation was taken.
   * @format GUID
   */
  originalMetaSiteId: string;
}

export interface AutomationTemplateSpecInfo {
  /**
   * Spec to conduct on for the automation.
   *
   * For internal use only.
   * @minLength 1
   * @maxLength 128
   */
  spec?: string;
  /**
   * Value the spec should return in order to affect the user with the operation
   * e.g. creation - show the users the automation template, update - show the users the updated automation template, deletion - not show the users the automation template
   * @minLength 1
   * @maxLength 128
   */
  variantValue?: string | null;
  /**
   * Version from which the experiment should decide whether to expose it to users when !conduct(spec).contains(variant_value)
   * For example, if 50 is set, version 49 and below (including 49) will not expose the automation template to the user.
   */
  experimentVersion?: number;
}

export interface CreateAutomationTemplateResponse {
  /** The created AutomationTemplate. */
  automationTemplate?: AutomationTemplate;
}

export interface GetAutomationTemplateRequest {
  /**
   * ID of the AutomationTemplate to retrieve.
   * @format GUID
   */
  automationTemplateId: string;
}

export interface GetAutomationTemplateResponse {
  /** The requested AutomationTemplate. */
  automationTemplate?: AutomationTemplate;
}

export interface UpdateAutomationTemplateRequest {
  /** AutomationTemplate to be updated, may be partial. */
  automationTemplate: AutomationTemplate;
  /**
   * ID of the app to which the automation template belongs.
   * @format GUID
   */
  appId: string;
}

export interface UpdateAutomationTemplateResponse {
  /** Updated AutomationTemplate. */
  automationTemplate?: AutomationTemplate;
}

export interface DeleteAutomationTemplateRequest {
  /**
   * App id of the AutomationTemplate to delete.
   * @format GUID
   */
  appId: string;
  /**
   * Component id of the AutomationTemplate to delete.
   * @format GUID
   */
  componentId: string;
}

export interface DeleteAutomationTemplateResponse {}

export interface ResolveAutomationTemplatesRequest {
  /** Query options. */
  query: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CommonCursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   */
  sort?: CommonSorting[];
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CommonCursorPaging;
}

export interface CommonSorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: CommonSortOrderWithLiterals;
  /**
   * Origin point for geo-distance sorting on a GEO field
   * results are ordered by distance from this point (ASC = nearest first, DESC = farthest first).
   */
  origin?: AddressLocation;
}

export enum CommonSortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type CommonSortOrderWithLiterals = CommonSortOrder | 'ASC' | 'DESC';

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CommonCursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface ResolveAutomationTemplatesResponse {
  /** Retrieved AutomationTemplates. */
  automationTemplates?: AutomationTemplate[];
  /** Paging metadata of the response. */
  pagingMetadata?: CommonCursorPagingMetadata;
}

export interface CommonCursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: CommonCursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface CommonCursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface QueryAutomationTemplatesForAppRequest {
  /** Query options. */
  query: CursorQuery;
  /**
   * ID of the app for which to query template automations.
   * @format GUID
   */
  appId: string;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryAutomationTemplatesForAppResponse {
  /** Retrieved AutomationTemplates. */
  automationTemplates?: AutomationTemplate[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
